import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Crown, Filter, UserCheck, ShieldCheck, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API, toast } from '@/App';
import DeliveryLabelDialog from '@/components/shared/DeliveryLabelDialog';
import { logClientError } from '@/utils/clientError';
import { getDeliveryLabelCapabilities } from '@/utils/deliveryLabels';

const ValidatedDeliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [livreurs, setLivreurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    city: 'all',
    status: 'all',
    isExpress: 'all'
  });
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedLivreur, setSelectedLivreur] = useState('');
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [selectedLabelDelivery, setSelectedLabelDelivery] = useState(null);

  useEffect(() => {
    loadDeliveries();
    loadLivreurs();
  }, [filters]);

  const loadDeliveries = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.city && filters.city !== 'all') params.append('city', filters.city);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.isExpress && filters.isExpress !== 'all') params.append('is_express', filters.isExpress === 'true');

      const response = await axios.get(`${API}/admin/deliveries/validated?${params.toString()}`);
      setDeliveries(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadLivreurs = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/by-role/livreur`);
      setLivreurs(response.data.filter((l) => l.is_active));
    } catch (error) {
      logClientError('admin.validated_deliveries.load_livreurs', error);
    }
  };

  const handleAssign = (delivery) => {
    setSelectedDelivery(delivery);
    setSelectedLivreur(delivery.livreur_id || '');
    setShowAssignDialog(true);
  };

  const handleOpenLabel = (delivery) => {
    setSelectedLabelDelivery(delivery);
    setShowLabelDialog(true);
  };

  const handleSaveAssignment = async () => {
    if (!selectedLivreur) {
      toast.error('Veuillez sélectionner un livreur');
      return;
    }

    try {
      await axios.patch(`${API}/admin/deliveries/${selectedDelivery.id}/assign-livreur`, null, {
        params: { livreur_id: selectedLivreur }
      });
      toast.success('Livreur assigné avec succés');
      setShowAssignDialog(false);
      loadDeliveries();
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      paid: { label: 'Payé', class: 'bg-blue-100 text-blue-700' },
      pickup_in_progress: { label: 'Ramassage', class: 'bg-indigo-100 text-indigo-700' },
      picked_up: { label: 'Récupéré', class: 'bg-purple-100 text-purple-700' },
      in_transit: { label: 'Transit', class: 'bg-cyan-100 text-cyan-700' },
      at_relay: { label: 'P-R', class: 'bg-orange-100 text-orange-700' },
      out_for_delivery: { label: 'Livraison', class: 'bg-green-100 text-green-700' },
      delivered: { label: 'Livré', class: 'bg-green-100 text-green-700' }
    };
    const info = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-700' };
    return <Badge className={info.class}>{info.label}</Badge>;
  };

  const getServiceTier = (delivery) => {
    if (delivery?.service_tier) return delivery.service_tier;
    return delivery?.is_express ? 'express' : 'standard';
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Package className="w-6 h-6 text-red-600" />
                Commandes Validées
              </CardTitle>
              <CardDescription>Gérer l'attribution des livreurs</CardDescription>
            </div>
            <Badge className="text-lg px-3 py-1">
              {deliveries.length} commande(s)
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Filtres:</span>
            </div>

            <Select value={filters.city} onValueChange={(val) => setFilters({ ...filters, city: val })}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Toutes les villes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les villes</SelectItem>
                <SelectItem value="BZV">Brazzaville</SelectItem>
                <SelectItem value="PNR">Pointe-Noire</SelectItem>
                <SelectItem value="KIN">Kinshasa</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.status} onValueChange={(val) => setFilters({ ...filters, status: val })}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="paid">Payé</SelectItem>
                <SelectItem value="in_transit">En transit</SelectItem>
                <SelectItem value="delivered">Livré</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.isExpress} onValueChange={(val) => setFilters({ ...filters, isExpress: val })}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tous les types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="true">Express uniquement</SelectItem>
                <SelectItem value="false">Standard uniquement</SelectItem>
              </SelectContent>
            </Select>

            {(filters.city !== 'all' || filters.status !== 'all' || filters.isExpress !== 'all') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ city: 'all', status: 'all', isExpress: 'all' })}
              >
                Réinitialiser
              </Button>
            )}
          </div>

          {deliveries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune commande validée</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Trajet</TableHead>
                  <TableHead>Tarif</TableHead>
                  <TableHead>Livreur</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((delivery) => {
                  const serviceTier = getServiceTier(delivery);
                  const isExpressTier = serviceTier === 'express';
                  const isInsured = Boolean(delivery.is_insured);
                  const labelCapabilities = getDeliveryLabelCapabilities(delivery);
                  const transportAmount = Number(delivery.transport_amount ?? delivery.cost ?? 0);
                  const expressSurchargeAmount = Number(delivery.express_surcharge_amount ?? 0);
                  const insuranceAmount = Number(delivery.insurance_amount ?? 0);
                  const totalAmount = Number(delivery.cost ?? 0);

                  return (
                    <TableRow key={delivery.id} className={isExpressTier ? 'bg-amber-50/30' : ''}>
                      <TableCell className="font-mono text-sm">
                        {delivery.tracking_code || `#${delivery.id.slice(0, 8)}`}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          {isExpressTier ? (
                            <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white flex items-center gap-1 w-fit">
                              <Crown className="w-3 h-3" />
                              EXPRESS
                            </Badge>
                          ) : (
                            <Badge variant="outline">Standard</Badge>
                          )}
                          {isInsured && (
                            <Badge className="bg-cyan-600 text-white flex items-center gap-1 w-fit">
                              <ShieldCheck className="w-3 h-3" />
                              Assurance
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{delivery.client_info.full_name}</TableCell>
                      <TableCell className="text-sm">
                        {delivery.pickup_city} -> {delivery.delivery_city}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="font-semibold">
                          {totalAmount.toLocaleString()} {delivery.currency}
                        </div>
                        {(delivery.transport_amount !== undefined || expressSurchargeAmount || insuranceAmount) && (
                          <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                            <p>Transport: {transportAmount.toLocaleString()} {delivery.currency}</p>
                            <p>Express: + {expressSurchargeAmount.toLocaleString()} {delivery.currency}</p>
                            <p>Assurance: + {insuranceAmount.toLocaleString()} {delivery.currency}</p>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {delivery.livreur_name ? (
                          <span className="text-sm">{delivery.livreur_name}</span>
                        ) : (
                          <Badge variant="secondary">Non assigné</Badge>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(delivery.status)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`assign-${delivery.id}`}
                            onClick={() => handleAssign(delivery)}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            {delivery.livreur_id ? 'Modifier' : 'Assigner'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`label-${delivery.id}`}
                            onClick={() => handleOpenLabel(delivery)}
                            disabled={!labelCapabilities.available}
                            title={!labelCapabilities.available ? labelCapabilities.reason : 'Voir l’étiquette'}
                          >
                            <ScanLine className="w-4 h-4 mr-1" />
                            Étiquette
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assigner un livreur</DialogTitle>
            <DialogDescription>
              Commande: {selectedDelivery?.tracking_code || selectedDelivery?.id.slice(0, 8)}
            </DialogDescription>
          </DialogHeader>

          <div>
            <label className="text-sm font-medium mb-2 block">Sélectionner un livreur</label>
            <Select value={selectedLivreur} onValueChange={setSelectedLivreur}>
              <SelectTrigger data-testid="livreur-select">
                <SelectValue placeholder="Choisir un livreur" />
              </SelectTrigger>
              <SelectContent>
                {livreurs
                  .filter((l) => !selectedDelivery || l.city === selectedDelivery.pickup_city)
                  .map((livreur) => (
                    <SelectItem key={livreur.id} value={livreur.id}>
                      {livreur.full_name} ({livreur.city})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Annuler
            </Button>
            <Button
              data-testid="confirm-assign-btn"
              className="bg-red-500 hover:bg-red-600"
              onClick={handleSaveAssignment}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeliveryLabelDialog
        open={showLabelDialog}
        onOpenChange={setShowLabelDialog}
        delivery={selectedLabelDelivery}
        allowPreferredFormat={false}
      />
    </div>
  );
};

export default ValidatedDeliveries;
